import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Typography,
} from '@material-ui/core'
import { CheckCircleOutline, InfoOutlined } from '@material-ui/icons'
import { ToggleButton } from '@material-ui/lab'
import { useEffect, useState } from 'react'

import AlertDialog from '@/components/alert-dialog'
import Button from '@/components/button'
import Loading from '@/components/loading'
import { MembersAutocomplete } from '@/components/share-project-modal/members-autocomplete'
import { useUpdateProjectShare } from '@/hooks/mutations/use-update-project-share'
import { useGetMembersByPrivateProject } from '@/hooks/queries/use-get-members-by-private-project'
import { useAuth } from '@/hooks/use-auth'
import User from '@/types/user'

import { useStyles } from './styles'
import { Privacy, Visibility } from './visibility'

type UserDTO = Omit<User, 'exp'>

interface MembersWithAccess extends UserDTO {
  role: string
  remove: boolean
}

interface ShareProjectModalProps extends DialogProps {
  project: { id: number; name: string; private: boolean } | null
  onClose: () => void
}

export function ShareProjectModal({
  project,
  onClose,
  ...props
}: ShareProjectModalProps) {
  const classes = useStyles()
  const [privacy, setPrivacy] = useState<Privacy>('corporate')
  const [selectedMembers, setSelectedMembers] = useState<UserDTO[]>([])
  const [membersWithAccess, setMembersWithAccess] = useState<
    MembersWithAccess[]
  >([])
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)
  const { user } = useAuth()

  const { isFetching } = useGetMembersByPrivateProject(project, {
    enabled: Boolean(project?.private) && props.open,
    onSuccess: (data) => {
      setMembersWithAccess(
        data.map((member) => ({
          ...member,
          remove: false,
        })),
      )
    },
    onError: () => {
      setMembersWithAccess([])
    },
  })

  const updateProjectShareMutation = useUpdateProjectShare()

  const handleUpdateProjectShare = () => {
    const users = [
      ...selectedMembers,
      ...membersWithAccess.filter((member) => !member.remove),
    ].map((member) => ({
      id: member.id,
      name: member.name,
      email: member.email,
    }))

    updateProjectShareMutation
      .mutateAsync({
        project: {
          ...project!,
          private: privacy === 'private',
        },
        users,
      })
      .finally(() => {
        setSelectedMembers([])
        setIsAlertModalOpen(true)
      })
  }

  const handleClose = () => {
    if (!updateProjectShareMutation.isLoading) onClose()
  }

  useEffect(() => {
    if (project) {
      setPrivacy(project.private ? 'private' : 'corporate')
      if (!project.private)
        setMembersWithAccess([{ ...user, remove: false, role: '' }])
    }
  }, [project])

  const getAlertProps = () => {
    if (updateProjectShareMutation.isError) {
      return {
        icon: <InfoOutlined />,
        title: 'Ocorreu um erro',
        subtitle: 'Não foi possível remover este membro',
        items: ['Tente novamente mais tarde ou entre em contato com a Cromai.'],
        variant: 'error' as const,
        confirm: 'Ok, entendi',
        handleConfirm: () => {
          setIsAlertModalOpen(false)
        },
      }
    }

    return {
      icon: <CheckCircleOutline />,
      title: 'Projeto compartilhado',
      subtitle: 'O compartilhamento deste projeto foi alterado',
      items: ['Agora uma ou mais pessoas poderão visualizar este projeto.'],
      variant: 'success' as const,
      confirm: 'Ok, entendi',
      handleConfirm: () => {
        onClose()
        setIsAlertModalOpen(false)
      },
    }
  }

  return (
    <>
      <Dialog
        fullWidth
        {...props}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography>
          <Typography variant='h2'>
            Gerenciar compartilhamento do &quot;{project?.name}&quot;
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Visibility
            title='Acesso geral'
            privacy={privacy}
            handleIsPrivate={setPrivacy}
          />
          <MembersAutocomplete
            selectedMembers={selectedMembers}
            handleSelectedMembers={setSelectedMembers}
            enabled={privacy === 'private'}
            filterData={membersWithAccess}
          />

          <Divider />

          <section className={classes.membersSection}>
            {isFetching ? (
              <Loading
                className={classes.loading}
                color='inherit'
                size='3rem'
              />
            ) : privacy === 'corporate' ||
              !membersWithAccess.filter((member) => member.id !== user.id)
                .length ? (
              <Typography variant='h3' color='textSecondary'>
                Toda a companhia terá acesso a este projeto
              </Typography>
            ) : (
              membersWithAccess
                .filter((member) => member.id !== user.id)
                .map((currentMember) => (
                  <Box
                    key={currentMember.id}
                    display='flex'
                    alignItems='end'
                    gridGap='4rem'
                  >
                    <Box>
                      <Typography>{currentMember.name}</Typography>
                      <Typography>{currentMember.email}</Typography>
                    </Box>
                    <Typography>{currentMember.role}</Typography>
                    <ToggleButton
                      value={currentMember.id}
                      aria-label={`Remover acesso do membro ${currentMember.id}`}
                      selected={currentMember.remove}
                      onChange={() =>
                        setMembersWithAccess(
                          membersWithAccess.map((member) =>
                            member.id === currentMember.id
                              ? { ...member, remove: !currentMember.remove }
                              : member,
                          ),
                        )
                      }
                    >
                      <span className='material-symbols-outlined'>
                        person_remove
                      </span>
                      <span>Remover acesso</span>
                    </ToggleButton>
                  </Box>
                ))
            )}
          </section>

          <Divider />
        </DialogContent>
        <DialogActions>
          <Button labelOnly onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateProjectShare}
            loading={updateProjectShareMutation.isLoading}
            disabled={isFetching}
          >
            Concluir
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog {...getAlertProps()} open={isAlertModalOpen} />
    </>
  )
}
